/**
 * Address Autocomplete Module
 *
 * Provides Radar API-based address autocomplete functionality for WooCommerce checkout forms.
 * Handles both billing and shipping address fields with support for dynamic form updates,
 * keyboard navigation, and proper cleanup to prevent memory leaks.
 */

import {$qs, $qsAll} from '../../@shared/ts/dom';
import {getFormattedStreetAddress} from '../../@shared/ts/address';
import {radarAddressAutocomplete, type RadarAddress} from '../../@shared/ts/radar-auto-complete';
import {FeatureFlag} from '../../@type/features';
import {fieldGetSet} from './checkout';
import {Feature} from './features';

type AutocompleteInstance = {
	input: HTMLInputElement;
	type: 'billing' | 'shipping';
	dropdown: HTMLDivElement;
	selectedIndex: number;
	suggestions: RadarAddress[];
	destroy: () => void;
};

const autocompleteInstances = new Map<string, AutocompleteInstance>();

/**
 * Checks if address autocomplete should be enabled based on feature flags
 */
function shouldEnableAddressAutocomplete() {
	if (Feature.enabled(FeatureFlag.ADDRESS_AUTOCOMPLETE)) {
		const activeLocations = Feature.metadata(FeatureFlag.ADDRESS_AUTOCOMPLETE, 'active_locations');

		if (activeLocations === 'default' || activeLocations === 'checkout_page_only') {
			return true;
		}
	}

	return false;
}

/**
 * Checks if an autocomplete dropdown is currently visible
 */
function isDropdownVisible(): boolean {
	for (const instance of autocompleteInstances.values()) {
		if (instance.dropdown.classList.contains('pp-autocomplete-visible')) {
			return true;
		}
	}

	return false;
}

/**
 * Checks if a field needs reinitialization
 */
function fieldNeedsInit(field: HTMLInputElement | undefined, fieldId: string): boolean {
	if (!field) {
		return false;
	}

	const existingInstance = autocompleteInstances.get(fieldId);
	return !existingInstance || existingInstance.input !== field;
}

/**
 * Cleans up a specific autocomplete instance
 */
function cleanupInstance(fieldId: string): void {
	const instance = autocompleteInstances.get(fieldId);
	if (instance) {
		instance.destroy();
		instance.dropdown.remove();
		autocompleteInstances.delete(fieldId);
	}
}

/**
 * Initializes fields that need setup
 */
function initializeFields(options: {
	billingField: HTMLInputElement | undefined;
	shippingField: HTMLInputElement | undefined;
	forceReinit: boolean;
	billingNeedsInit: boolean;
	shippingNeedsInit: boolean;
}): void {
	if (options.billingField && (options.forceReinit || options.billingNeedsInit)) {
		setupAutocomplete(options.billingField, 'billing');
	}

	if (options.shippingField && (options.forceReinit || options.shippingNeedsInit)) {
		setupAutocomplete(options.shippingField, 'shipping');
	}
}

/**
 * Initializes Radar autocomplete with retry logic for WooCommerce dynamic forms.
 *
 * Handles cases where form fields may not be immediately available due to WooCommerce's
 * dynamic form updates. Uses exponential backoff for retries and only reinitializes
 * when necessary to preserve user experience (e.g., keeps dropdown visible during payment method updates).
 *
 * @param retryCount - Current retry attempt (starts at 0)
 * @param forceReinit - Force reinitialization even if fields haven't changed
 */
function initializeRadarAutocomplete(retryCount = 0, forceReinit = false): void {
	const maxRetries = 5;
	const retryDelay = 200 * (2 ** retryCount);

	// Check for required fields
	const billingField = $qs<HTMLInputElement>('form [name="billing_address_1"]') ?? undefined;
	const shippingField = $qs<HTMLInputElement>('form [name="shipping_address_1"]') ?? undefined;

	if (!billingField && !shippingField) {
		if (retryCount < maxRetries) {
			setTimeout(() => {
				initializeRadarAutocomplete(retryCount + 1, forceReinit);
			}, retryDelay);
		} else {
			console.warn('[PeachPay Autocomplete] Address fields not found after maximum retries');
		}

		return;
	}

	// Check if we need to reinitialize
	const billingNeedsInit = fieldNeedsInit(billingField, 'billing_address_1');
	const shippingNeedsInit = fieldNeedsInit(shippingField, 'shipping_address_1');

	// If dropdown is visible and fields haven't changed, don't reinitialize
	if (!forceReinit && !billingNeedsInit && !shippingNeedsInit && isDropdownVisible()) {
		return;
	}

	// Only clean up instances that need reinitialization
	if (forceReinit || billingNeedsInit || shippingNeedsInit) {
		if (billingNeedsInit) {
			cleanupInstance('billing_address_1');
		}

		if (shippingNeedsInit) {
			cleanupInstance('shipping_address_1');
		}

		initializeFields({
			billingField,
			shippingField,
			forceReinit,
			billingNeedsInit,
			shippingNeedsInit,
		});
	}
}

/**
 * Sets up autocomplete for a specific input field.
 *
 * Creates event listeners for input, keyboard navigation, focus, and blur events.
 * Handles API calls with debouncing and displays suggestions in a custom dropdown.
 *
 * @param input - The address input field to attach autocomplete to
 * @param type - Whether this is a billing or shipping address field
 */
function setupAutocomplete(input: HTMLInputElement, type: 'billing' | 'shipping'): void {
	const fieldId = `${type}_address_1`;

	// Skip if already initialized
	if (autocompleteInstances.has(fieldId)) {
		return;
	}

	// Create autocomplete dropdown
	const dropdown = createAutocompleteDropdown();

	// Handle input events
	const handleInput = async (event: Event) => {
		const target = event.target as HTMLInputElement;
		const query = target.value.trim();

		if (query.length < 3) {
			hideAutocompleteDropdown(dropdown);
			return;
		}

		// Show loading state
		showLoadingState(dropdown, input);

		try {
			const suggestions = await radarAddressAutocomplete(query);
			const instance = autocompleteInstances.get(fieldId);
			if (instance) {
				instance.suggestions = suggestions;
				instance.selectedIndex = -1;
			}

			if (suggestions.length > 0) {
				showAutocompleteDropdown(dropdown, suggestions, input, type);
			} else {
				showNoResultsState(dropdown, input);
			}
		} catch (error: unknown) {
			console.error('[PeachPay Autocomplete] Error fetching suggestions:', error);
			showNoResultsState(dropdown, input);
		}
	};

	// Handle keyboard navigation
	const handleKeyDown = (event: KeyboardEvent) => {
		const instance = autocompleteInstances.get(fieldId);
		if (!instance || !dropdown.classList.contains('pp-autocomplete-visible')) {
			return;
		}

		switch (event.key) {
			case 'ArrowDown': {
				event.preventDefault();
				instance.selectedIndex = Math.min(instance.selectedIndex + 1, instance.suggestions.length - 1);
				updateSelectedSuggestion(dropdown, instance.selectedIndex);
				break;
			}

			case 'ArrowUp': {
				event.preventDefault();
				instance.selectedIndex = Math.max(instance.selectedIndex - 1, -1);
				updateSelectedSuggestion(dropdown, instance.selectedIndex);
				break;
			}

			case 'Enter': {
				event.preventDefault();
				if (instance.selectedIndex >= 0 && instance.selectedIndex < instance.suggestions.length) {
					const selectedAddress = instance.suggestions[instance.selectedIndex];
					if (selectedAddress) {
						selectAddress(selectedAddress, type);
					}
				}

				break;
			}

			case 'Escape': {
				hideAutocompleteDropdown(dropdown);
				break;
			}

			default: {
				break;
			}
		}
	};

	// Handle focus/blur
	const handleFocus = () => {
		const instance = autocompleteInstances.get(fieldId);
		if (instance && instance.suggestions.length > 0 && input.value.trim().length >= 3) {
			showAutocompleteDropdown(dropdown, instance.suggestions, input, type);
		}
	};

	const handleBlur = () => {
		// Delay to allow click events on dropdown to fire
		setTimeout(() => {
			hideAutocompleteDropdown(dropdown);
		}, 200);
	};

	input.addEventListener('input', handleInput);
	input.addEventListener('keydown', handleKeyDown);
	input.addEventListener('focus', handleFocus);
	input.addEventListener('blur', handleBlur);

	// Store instance for cleanup
	autocompleteInstances.set(fieldId, {
		input,
		type,
		dropdown,
		selectedIndex: -1,
		suggestions: [],
		destroy() {
			input.removeEventListener('input', handleInput);
			input.removeEventListener('keydown', handleKeyDown);
			input.removeEventListener('focus', handleFocus);
			input.removeEventListener('blur', handleBlur);
		},
	});
}

/**
 * Creates the autocomplete dropdown element with proper styling and positioning.
 */
function createAutocompleteDropdown(): HTMLDivElement {
	const dropdown = document.createElement('div');
	dropdown.className = 'pp-autocomplete-dropdown';
	dropdown.style.cssText = `
		position: absolute;
		background: white;
		border: 1px solid #ccc;
		border-radius: 4px;
		box-shadow: 0 2px 10px rgba(0,0,0,0.1);
		max-height: 300px;
		overflow-y: auto;
		z-index: 10000;
		display: none;
	`;
	document.body.append(dropdown);
	return dropdown;
}

/**
 * Shows loading state while fetching address suggestions from the API.
 */
function showLoadingState(dropdown: HTMLDivElement, input: HTMLInputElement): void {
	dropdown.innerHTML = '';
	const loadingItem = document.createElement('div');
	loadingItem.className = 'pp-autocomplete-loading';
	loadingItem.style.cssText = `
		padding: 15px;
		text-align: center;
		color: #666;
		font-style: italic;
	`;
	loadingItem.textContent = 'Searching addresses...';
	dropdown.append(loadingItem);

	// Position dropdown below input
	const rect = input.getBoundingClientRect();
	dropdown.style.top = `${rect.bottom + window.scrollY}px`;
	dropdown.style.left = `${rect.left + window.scrollX}px`;
	dropdown.style.width = `${rect.width}px`;
	dropdown.style.display = 'block';
	dropdown.classList.add('pp-autocomplete-visible');
}

/**
 * Shows "no results found" message when the search returns empty results.
 */
function showNoResultsState(dropdown: HTMLDivElement, input: HTMLInputElement): void {
	dropdown.innerHTML = '';
	const noResultsItem = document.createElement('div');
	noResultsItem.className = 'pp-autocomplete-no-results';
	noResultsItem.style.cssText = `
		padding: 15px;
		text-align: center;
		color: #999;
		font-style: italic;
	`;
	noResultsItem.textContent = 'No addresses found';
	dropdown.append(noResultsItem);

	// Position dropdown below input
	const rect = input.getBoundingClientRect();
	dropdown.style.top = `${rect.bottom + window.scrollY}px`;
	dropdown.style.left = `${rect.left + window.scrollX}px`;
	dropdown.style.width = `${rect.width}px`;
	dropdown.style.display = 'block';
	dropdown.classList.add('pp-autocomplete-visible');
}

/**
 * Displays the autocomplete dropdown with address suggestions.
 *
 * Creates clickable items for each suggestion and positions the dropdown
 * below the input field. Handles mouse hover for keyboard navigation sync.
 */
function showAutocompleteDropdown(
	dropdown: HTMLDivElement,
	suggestions: RadarAddress[],
	input: HTMLInputElement,
	type: 'billing' | 'shipping',
): void {
	dropdown.innerHTML = '';
	for (const [index, suggestion] of suggestions.entries()) {
		const item = document.createElement('div');
		item.className = 'pp-autocomplete-item';
		item.style.cssText = `
			padding: 10px;
			cursor: pointer;
			border-bottom: 1px solid #eee;
		`;
		item.textContent = suggestion.formattedAddress;
		item.addEventListener('mouseenter', () => {
			const instance = autocompleteInstances.get(`${type}_address_1`);
			if (instance) {
				instance.selectedIndex = index;
				updateSelectedSuggestion(dropdown, index);
			}
		});
		item.addEventListener('click', () => {
			selectAddress(suggestion, type);
		});
		dropdown.append(item);
	}

	// Position dropdown below input
	const rect = input.getBoundingClientRect();
	dropdown.style.top = `${rect.bottom + window.scrollY}px`;
	dropdown.style.left = `${rect.left + window.scrollX}px`;
	dropdown.style.width = `${rect.width}px`;
	dropdown.style.display = 'block';
	dropdown.classList.add('pp-autocomplete-visible');
}

/**
 * Hides the autocomplete dropdown
 */
function hideAutocompleteDropdown(dropdown: HTMLDivElement): void {
	dropdown.style.display = 'none';
	dropdown.classList.remove('pp-autocomplete-visible');
}

/**
 * Updates the selected suggestion highlight
 */
function updateSelectedSuggestion(dropdown: HTMLDivElement, selectedIndex: number): void {
	const items = dropdown.querySelectorAll('.pp-autocomplete-item');
	for (const [index, item] of items.entries()) {
		if (index === selectedIndex) {
			(item as HTMLElement).style.backgroundColor = '#f0f0f0';
		} else {
			(item as HTMLElement).style.backgroundColor = 'white';
		}
	}
}

/**
 * Selects an address and fills in the form
 */
function selectAddress(address: RadarAddress, type: 'billing' | 'shipping'): void {
	const instance = autocompleteInstances.get(`${type}_address_1`);
	if (instance) {
		hideAutocompleteDropdown(instance.dropdown);
	}

	// Set the input value
	const input = $qs<HTMLInputElement>(`form [name="${type}_address_1"]`);
	if (input) {
		input.value = address.formattedAddress;
	}

	// Fill in address using existing logic
	fillInAddress(address, type);
}

/**
 * Cleans up all autocomplete instances
 */
function cleanupAutocomplete(): void {
	for (const instance of autocompleteInstances.values()) {
		instance.destroy();
		instance.dropdown.remove();
	}

	autocompleteInstances.clear();
}

/**
 * Observes checkout form for DOM changes to detect when address fields are added or replaced.
 *
 * Uses MutationObserver as a fallback mechanism to handle WooCommerce's dynamic form updates
 * that may not trigger standard events. Only reinitializes when address fields actually change.
 */
function observeCheckoutForm(): void {
	const checkoutForm = document.querySelector('form.checkout');
	if (!checkoutForm) {
		return;
	}

	const observer = new MutationObserver(mutations => {
		let addressFieldsChanged = false;

		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node.nodeType === Node.ELEMENT_NODE) {
					const element = node as Element;
					const addressField = element.querySelector?.('[name*="address_1"]');
					if (addressField) {
						addressFieldsChanged = true;
					}
				}
			}
		}

		if (addressFieldsChanged) {
			// Debounce to avoid multiple rapid initializations
			// Force reinit when address fields are added/changed
			clearTimeout((window as any).autocompleteReinitTimeout);
			(window as any).autocompleteReinitTimeout = setTimeout(() => {
				initializeRadarAutocomplete(0, true);
			}, 150);
		}
	});

	observer.observe(checkoutForm, {
		childList: true,
		subtree: true,
	});
}

/**
 * Initializes address autocomplete functionality.
 *
 * Sets up Radar API autocomplete for billing and shipping address fields.
 * Handles WooCommerce form updates, country changes, and provides proper cleanup.
 * Only reinitializes when necessary to preserve user experience (e.g., keeps dropdown
 * visible during payment method updates).
 */
export function initAddressAutocomplete(): void {
	if (!shouldEnableAddressAutocomplete()) {
		return;
	}

	initializeRadarAutocomplete();

	// Reinitialize on checkout updates, but only if fields actually changed
	jQuery(document.body).on('updated_checkout init_checkout', () => {
		initializeRadarAutocomplete(0, false);
	});

	// Force reinit on country changes since fields may be replaced
	jQuery(document.body).on(
		'change',
		'select[name="billing_country"], select[name="shipping_country"]',
		() => {
			setTimeout(() => {
				initializeRadarAutocomplete(0, true);
			}, 100);
		},
	);

	observeCheckoutForm();
	window.addEventListener('beforeunload', cleanupAutocomplete);

	// Prevent form submission when Enter is pressed in address field
	$qsAll('#pp-info-form', $form => {
		$form.addEventListener('keypress', preventSubmitListener);
	});
}

/**
 * Prevents form submission when Enter key is pressed in address input fields.
 */
function preventSubmitListener(event: Event) {
	const key = (event as KeyboardEvent).key || 0;
	if (key === 'Enter') {
		event.preventDefault();
	}
}

/**
 * Fills in address fields from Radar address data.
 *
 * Maps Radar address components to WooCommerce form fields. Handles US and common
 * European countries. State/province filling may need country-specific adjustments
 * due to inconsistent shortcodes between Radar and WooCommerce.
 *
 * @param address - The selected Radar address
 * @param section - Address section ('billing' or 'shipping')
 */
// eslint-disable-next-line complexity
function fillInAddress(address: RadarAddress, section: string): void {
	const address1Field = fieldGetSet(`${section}_address_1`);
	const address2Field = fieldGetSet(`${section}_address_2`);
	const postalField = fieldGetSet(`${section}_postcode`);
	const cityField = fieldGetSet(`${section}_city`);
	const stateField = fieldGetSet(`${section}_state`);
	const provinceField = fieldGetSet(`${section}_province`);

	// Clear all fields, to make it clear to the user what wasn't updated
	if (address1Field) {
		address1Field.set('');
	}

	if (address2Field) {
		address2Field.set('');
	}

	if (postalField) {
		postalField.set('');
	}

	if (cityField) {
		cityField.set('');
	}

	if (stateField) {
		stateField.set('');
	}

	if (provinceField) {
		provinceField.set('');
	}

	if (!address) {
		return;
	}

	// Set country field first so province/state field can update accordingly.
	const countryField = fieldGetSet(`${section}_country`);
	if (countryField && address.countryCode) {
		countryField.set(address.countryCode);
		jQuery(countryField).trigger('change');
	}

	// Set address line 1 (use formatted address or construct from components)
	if (address1Field) {
		const address1 = address.formattedAddress ?? (address.number && address.street ? `${address.number} ${address.street}`.trim() : address.street ?? '');
		if (address1) {
			address1Field.set(getFormattedStreetAddress(address1));
		}
	}

	// Set city
	if (cityField && address.city) {
		cityField.set(address.city);
	}

	// Set postal code
	if (postalField && address.postalCode) {
		postalField.set(address.postalCode);
	}

	// Only autofill state/province value if it isn't hidden by wc rule and if the autofill data is valid.
	const stateValue = address.stateCode ?? '';
	if (stateValue) {
		// Fill in both fields, since both will likely be found by the querySelector
		if (stateField) {
			stateField.set(stateValue);
			jQuery(stateField).trigger('change');
		}

		if (provinceField) {
			provinceField.set(stateValue);
			jQuery(provinceField).trigger('change');
		}
	}

	// After filling the form with address components from the Autocomplete
	// prediction, set cursor focus on the second address line to encourage
	// entry of subpremise information such as apartment, unit, or floor number.
	const address2Element = $qs<HTMLInputElement>(`[name="${section}_address_2"]:not(.hide)`);
	if (address2Element) {
		address2Element.focus();
	}
}
